import { EventSourcePolyfill } from "event-source-polyfill";
import { baseUrl, getMercureToken } from "services/api";

export const getMercureHubUrl = () =>
  process.env.REACT_APP_MERCURE_URL ||
  (baseUrl ? `${baseUrl.replace(/\/$/, "")}/.well-known/mercure` : "");

/** @typedef {'connecting'|'open'|'reconnecting'} MercureConnectionStatus */

const ES_CLOSED = typeof EventSource !== "undefined" ? EventSource.CLOSED : 2;

const DEFAULT_RECONNECT_DELAYS_MS = [1000, 2000, 5000, 10000, 20000, 30000];

/**
 * @param {string|string[]} topicOrTopics Один топик или несколько (Mercure: несколько query topic=)
 */
export const createMercureEventSource = async (topicOrTopics) => {
  try {
    const { token } = await getMercureToken();
    if (!token) return null;
    const mercureUrl = getMercureHubUrl();
    if (!mercureUrl) return null;
    const url = new URL(mercureUrl);
    const topics = Array.isArray(topicOrTopics) ? topicOrTopics : [topicOrTopics];
    for (const t of topics) {
      url.searchParams.append("topic", t);
    }
    return new EventSourcePolyfill(url.toString(), {
      headers: { Authorization: `Bearer ${token}` },
      heartbeatTimeout: 30000,
    });
  } catch {
    return null;
  }
};

/**
 * Подписка на Mercure с отслеживанием состояния и переподключением при обрыве.
 *
 * @param {string|string[]} topicOrTopics
 * @param {{
 *   onMessage: (event: MessageEvent) => void,
 *   onStatus?: (status: MercureConnectionStatus) => void,
 *   reconnectDelaysMs?: number[],
 * }} options
 * @returns {{ close: () => void }}
 */
export const subscribeMercureReconnect = (topicOrTopics, options) => {
  const { onMessage, onStatus, reconnectDelaysMs = DEFAULT_RECONNECT_DELAYS_MS } = options || {};
  const topics = topicOrTopics;

  let stopped = false;
  let es = null;
  let reconnectAttempt = 0;
  let reconnectTimer = null;
  let connectBusy = false;

  const emit = (/** @type {MercureConnectionStatus} */ status) => {
    if (!stopped) onStatus?.(status);
  };

  const clearReconnectTimer = () => {
    if (reconnectTimer != null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const closeCurrent = () => {
    if (es) {
      try {
        es.close();
      } catch {
        /* ignore */
      }
      es = null;
    }
  };

  const scheduleReconnect = () => {
    if (stopped) return;
    clearReconnectTimer();
    const idx = Math.min(reconnectAttempt, reconnectDelaysMs.length - 1);
    const delayMs = reconnectDelaysMs[idx];
    reconnectAttempt += 1;
    emit("reconnecting");
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      openConnection();
    }, delayMs);
  };

  const attachTo = (eventSource) => {
    eventSource.onmessage = onMessage;
    eventSource.onopen = () => {
      reconnectAttempt = 0;
      clearReconnectTimer();
      emit("open");
    };
    eventSource.onerror = () => {
      if (stopped) return;
      if (eventSource.readyState === ES_CLOSED) {
        if (es === eventSource) {
          es = null;
        }
        if (!stopped) {
          scheduleReconnect();
        }
      } else {
        emit("connecting");
      }
    };
  };

  const openConnection = async () => {
    if (stopped || es || connectBusy) return;
    connectBusy = true;
    const isRetry = reconnectAttempt > 0;
    emit(isRetry ? "reconnecting" : "connecting");
    try {
      const eventSource = await createMercureEventSource(topics);
      if (stopped) {
        eventSource?.close();
        return;
      }
      if (!eventSource) {
        scheduleReconnect();
        return;
      }
      es = eventSource;
      attachTo(eventSource);
    } finally {
      connectBusy = false;
    }
  };

  const onBrowserOnline = () => {
    if (stopped || es) return;
    reconnectAttempt = 0;
    clearReconnectTimer();
    openConnection();
  };

  const onVisibilityChange = () => {
    if (stopped || document.visibilityState !== "visible" || es) return;
    reconnectAttempt = 0;
    clearReconnectTimer();
    openConnection();
  };

  window.addEventListener("online", onBrowserOnline);
  document.addEventListener("visibilitychange", onVisibilityChange);

  openConnection();

  return {
    close() {
      stopped = true;
      clearReconnectTimer();
      window.removeEventListener("online", onBrowserOnline);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      closeCurrent();
    },
  };
};
